import React, { useState, useEffect, useCallback, useRef } from 'react';
import './PartnerDashboard.css';
import MonthlyExpenses from './MonthlyExpenses';

// =============================================================================
// TIMEZONE HELPERS (inline from TimezoneContext.js, hardcoded to Asia/Nicosia)
// =============================================================================

const TIMEZONE = 'Asia/Nicosia';

function getTimezoneOffsetMs(date, timezone) {
  const utcStr = date.toLocaleString('en-US', { timeZone: 'UTC' });
  const tzStr = date.toLocaleString('en-US', { timeZone: timezone });
  return new Date(tzStr).getTime() - new Date(utcStr).getTime();
}

function getTzNow(timezone) {
  return new Date(Date.now() + getTimezoneOffsetMs(new Date(), timezone));
}

function fmtDateUTC(d) {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

function localDateToUTC(dateStr, timezone) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const localMidnight = new Date(Date.UTC(y, m - 1, d));
  const offset = getTimezoneOffsetMs(localMidnight, timezone);
  return new Date(localMidnight.getTime() - offset);
}

function getDateRangeForScope(scope, customStart, customEnd) {
  const now = new Date();
  const tzNow = getTzNow(TIMEZONE);
  const todayStr = fmtDateUTC(tzNow);

  const makeRange = (startStr, endStr) => {
    const start = localDateToUTC(startStr, TIMEZONE);
    const [ey, em, ed] = endStr.split('-').map(Number);
    const endLocal = new Date(Date.UTC(ey, em - 1, ed + 1));
    const endOffset = getTimezoneOffsetMs(endLocal, TIMEZONE);
    const end = new Date(endLocal.getTime() - endOffset);
    return { startTimestamp: start.getTime(), endTimestamp: end.getTime() };
  };

  switch (scope) {
    case 'today':
      return makeRange(todayStr, todayStr);
    case 'yesterday': {
      const yd = new Date(tzNow);
      yd.setUTCDate(yd.getUTCDate() - 1);
      return makeRange(fmtDateUTC(yd), fmtDateUTC(yd));
    }
    case 'week': {
      const weekStart = new Date(tzNow);
      weekStart.setUTCDate(tzNow.getUTCDate() - ((tzNow.getUTCDay() + 6) % 7));
      return makeRange(fmtDateUTC(weekStart), todayStr);
    }
    case 'last-week': {
      const thisMonday = new Date(tzNow);
      thisMonday.setUTCDate(tzNow.getUTCDate() - ((tzNow.getUTCDay() + 6) % 7));
      const lwStart = new Date(thisMonday);
      lwStart.setUTCDate(thisMonday.getUTCDate() - 7);
      const lwEnd = new Date(thisMonday);
      lwEnd.setUTCDate(thisMonday.getUTCDate() - 1);
      return makeRange(fmtDateUTC(lwStart), fmtDateUTC(lwEnd));
    }
    case 'month': {
      const monthStart = new Date(Date.UTC(tzNow.getUTCFullYear(), tzNow.getUTCMonth(), 1));
      return makeRange(fmtDateUTC(monthStart), todayStr);
    }
    case 'last-month': {
      const lmStart = new Date(Date.UTC(tzNow.getUTCFullYear(), tzNow.getUTCMonth() - 1, 1));
      const lmEnd = new Date(Date.UTC(tzNow.getUTCFullYear(), tzNow.getUTCMonth(), 0));
      return makeRange(fmtDateUTC(lmStart), fmtDateUTC(lmEnd));
    }
    case 'this-year': {
      const yearStart = new Date(Date.UTC(tzNow.getUTCFullYear(), 0, 1));
      return makeRange(fmtDateUTC(yearStart), todayStr);
    }
    case 'custom':
      if (customStart && customEnd) return makeRange(customStart, customEnd);
      return makeRange(todayStr, todayStr);
    default:
      return makeRange(todayStr, todayStr);
  }
}

// =============================================================================
// FORMAT HELPERS
// =============================================================================

function formatCents(cents, currency) {
  const abs = Math.abs(cents);
  const formatted = (abs / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const symbol = currency === 'USD' ? '$' : '\u20AC';
  const sign = cents < 0 ? '-' : '';
  return `${sign}${symbol}${formatted}`;
}

function formatNumber(num) {
  return num.toLocaleString('en-US');
}

// =============================================================================
// SCOPES CONFIG
// =============================================================================

const SCOPES = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'week', label: 'This Week' },
  { key: 'last-week', label: 'Last Week' },
  { key: 'month', label: 'This Month' },
  { key: 'last-month', label: 'Last Month' },
  { key: 'this-year', label: 'This Year' },
  { key: 'custom', label: 'Custom' },
];

// =============================================================================
// COMPONENT
// =============================================================================

function PartnerDashboard({ user, token, onLogout }) {
  const [theme, setTheme] = useState(() => localStorage.getItem('pv-theme') || 'light');
  const [view, setView] = useState(() => {
    try { return localStorage.getItem('pv-view') === 'expenses' ? 'expenses' : 'dashboard'; } catch { return 'dashboard'; }
  });
  const selectView = (v) => {
    setView(v);
    try { localStorage.setItem('pv-view', v); } catch { /* storage unavailable */ }
  };
  const [scope, setScope] = useState('today');
  const [currency, setCurrency] = useState('USD');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Debts state
  const [debts, setDebts] = useState([]);
  const [showDebtModal, setShowDebtModal] = useState(false);
  const [editingDebt, setEditingDebt] = useState(null);
  const [debtForm, setDebtForm] = useState({ creditor_name: '', original_amount: '', remaining_amount: '', expected_payment_date: '', notes: '' });

  // Rolling Reserves state
  const [rollingReserves, setRollingReserves] = useState([]);
  const [showRrModal, setShowRrModal] = useState(false);
  const [editingRr, setEditingRr] = useState(null);
  const [rrForm, setRrForm] = useState({ provider_name: '', held_amount: '', release_date: '', notes: '' });

  // The query whose figures are wanted now; answers for any other query (a scope the user
  // already left) are dropped so they can never overwrite the current scope's cards.
  const statsQueryRef = useRef(null);
  const [updatedAt, setUpdatedAt] = useState(null);

  const fetchStats = useCallback(async () => {
    const range = getDateRangeForScope(scope, customStart, customEnd);
    const q = `startTimestamp=${range.startTimestamp}&endTimestamp=${range.endTimestamp}`;
    // A different scope/range: clear the cards so the previous scope's numbers are not shown under it
    if (statsQueryRef.current !== q) setStats(null);
    statsQueryRef.current = q;
    setLoading(true);
    setError('');

    try {
      const res = await fetch(
        `/api/dashboard/stats?${q}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (statsQueryRef.current !== q) return;

      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          onLogout();
          return;
        }
        // The Mirror only ever shows live figures from the main system. If those are
        // unavailable we clear the cards rather than leave stale numbers on screen.
        let msg = 'Failed to load stats';
        try {
          const body = await res.json();
          if (body && body.error) msg = body.error;
        } catch { /* non-JSON error body */ }
        setStats(null);
        throw new Error(msg);
      }

      const data = await res.json();
      if (statsQueryRef.current !== q) return;
      setStats(data);
      setUpdatedAt(new Date());
    } catch (err) {
      if (statsQueryRef.current === q) setError(err.message || 'Failed to load data');
    } finally {
      if (statsQueryRef.current === q) setLoading(false);
    }
  }, [scope, customStart, customEnd, token, onLogout]);

  useEffect(() => {
    if (scope !== 'custom' || (customStart && customEnd)) {
      fetchStats();
    }
  }, [fetchStats, scope, customStart, customEnd]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (scope !== 'custom' || (customStart && customEnd)) {
        fetchStats();
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [fetchStats, scope, customStart, customEnd]);

  // ── Debts fetch & CRUD ──
  const fetchDebts = useCallback(async () => {
    try {
      const res = await fetch('/api/debts', { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setDebts(data.debts || []);
      }
    } catch (err) { console.error('Failed to fetch debts:', err); }
  }, [token]);

  const saveDebt = async () => {
    const body = {
      creditor_name: debtForm.creditor_name,
      original_amount_cents: Math.round(parseFloat(debtForm.original_amount || 0) * 100),
      remaining_amount_cents: Math.round(parseFloat(debtForm.remaining_amount || debtForm.original_amount || 0) * 100),
      currency: 'EUR',
      expected_payment_date: debtForm.expected_payment_date || null,
      notes: debtForm.notes || null
    };
    try {
      const url = editingDebt ? `/api/debts/${editingDebt.id}` : '/api/debts';
      const method = editingDebt ? 'PUT' : 'POST';
      await fetch(url, { method, headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify(body) });
      setShowDebtModal(false);
      setEditingDebt(null);
      setDebtForm({ creditor_name: '', original_amount: '', remaining_amount: '', expected_payment_date: '', notes: '' });
      fetchDebts();
    } catch (err) { console.error('Failed to save debt:', err); }
  };

  const deleteDebt = async (id) => {
    if (!window.confirm('Delete this debt entry?')) return;
    try {
      await fetch(`/api/debts/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
      fetchDebts();
    } catch (err) { console.error('Failed to delete debt:', err); }
  };

  const openEditDebt = (debt) => {
    setEditingDebt(debt);
    setDebtForm({
      creditor_name: debt.creditor_name,
      original_amount: (debt.original_amount_cents / 100).toFixed(2),
      remaining_amount: (debt.remaining_amount_cents / 100).toFixed(2),
      expected_payment_date: debt.expected_payment_date ? debt.expected_payment_date.split('T')[0] : '',
      notes: debt.notes || ''
    });
    setShowDebtModal(true);
  };

  // ── Rolling Reserves fetch & CRUD ──
  const fetchRollingReserves = useCallback(async () => {
    try {
      const res = await fetch('/api/rolling-reserves', { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setRollingReserves(data.rolling_reserves || []);
      }
    } catch (err) { console.error('Failed to fetch RR:', err); }
  }, [token]);

  const saveRr = async () => {
    const body = {
      provider_name: rrForm.provider_name,
      held_amount_cents: Math.round(parseFloat(rrForm.held_amount || 0) * 100),
      currency: 'EUR',
      release_date: rrForm.release_date || null,
      notes: rrForm.notes || null
    };
    try {
      const url = editingRr ? `/api/rolling-reserves/${editingRr.id}` : '/api/rolling-reserves';
      const method = editingRr ? 'PUT' : 'POST';
      await fetch(url, { method, headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify(body) });
      setShowRrModal(false);
      setEditingRr(null);
      setRrForm({ provider_name: '', held_amount: '', release_date: '', notes: '' });
      fetchRollingReserves();
    } catch (err) { console.error('Failed to save RR:', err); }
  };

  const deleteRr = async (id) => {
    if (!window.confirm('Delete this rolling reserve entry?')) return;
    try {
      await fetch(`/api/rolling-reserves/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
      fetchRollingReserves();
    } catch (err) { console.error('Failed to delete RR:', err); }
  };

  const openEditRr = (rr) => {
    setEditingRr(rr);
    setRrForm({
      provider_name: rr.provider_name,
      held_amount: (rr.held_amount_cents / 100).toFixed(2),
      release_date: rr.release_date ? rr.release_date.split('T')[0] : '',
      notes: rr.notes || ''
    });
    setShowRrModal(true);
  };

  // Load debts and RR on mount, then every 60 seconds like the stats cards (they are edited in
  // Settlement Hub on memcules.com, so an open page must pick up changes without a reload)
  useEffect(() => {
    fetchDebts();
    fetchRollingReserves();
    const interval = setInterval(() => { fetchDebts(); fetchRollingReserves(); }, 60000);
    return () => clearInterval(interval);
  }, [fetchDebts, fetchRollingReserves]);

  const getVal = (eurField, usdField) => {
    if (!stats) return 0;
    return currency === 'EUR' ? (stats[eurField] || 0) : (stats[usdField] || stats[eurField] || 0);
  };

  const sym = currency === 'EUR' ? '\u20AC' : '$';

  const toggleTheme = () => {
    const next = theme === 'light' ? 'dark' : 'light';
    setTheme(next);
    localStorage.setItem('pv-theme', next);
  };

  return (
    <div className={`pv-dashboard ${theme === 'dark' ? 'pv-dark' : 'pv-light'}`}>
      {/* Header */}
      <div className="pv-header">
        <div className="pv-header-left">
          <h1 className="pv-logo">Finance Dashboard</h1>
        </div>
        <div className="pv-header-right">
          <button className="pv-theme-toggle" onClick={toggleTheme} title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}>
            {theme === 'light' ? '\u263E' : '\u2600'}
          </button>
          <div className="pv-currency-toggle">
            <button
              className={currency === 'EUR' ? 'active' : ''}
              onClick={() => setCurrency('EUR')}
            >EUR</button>
            <button
              className={currency === 'USD' ? 'active' : ''}
              onClick={() => setCurrency('USD')}
            >USD</button>
          </div>
          <span className="pv-user-name">{user.name || user.email}</span>
          <button className="pv-logout-btn" onClick={onLogout}>Logout</button>
        </div>
      </div>

      {/* Page tabs */}
      <div className="pv-view-tabs">
        <button className={`pv-view-tab ${view === 'dashboard' ? 'active' : ''}`} onClick={() => selectView('dashboard')}>Dashboard</button>
        <button className={`pv-view-tab ${view === 'expenses' ? 'active' : ''}`} onClick={() => selectView('expenses')}>Monthly Expenses</button>
      </div>

      {view === 'expenses' && <MonthlyExpenses token={token} onLogout={onLogout} />}

      {view === 'dashboard' && (<>
      {/* Scope selector */}
      <div className="pv-scope-bar">
        {SCOPES.map(s => (
          <button
            key={s.key}
            className={`pv-scope-btn ${scope === s.key ? 'active' : ''}`}
            onClick={() => setScope(s.key)}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Custom date inputs */}
      {scope === 'custom' && (
        <div className="pv-custom-dates">
          <input
            type="date"
            value={customStart}
            onChange={(e) => setCustomStart(e.target.value)}
          />
          <span className="pv-date-to">to</span>
          <input
            type="date"
            value={customEnd}
            onChange={(e) => setCustomEnd(e.target.value)}
          />
        </div>
      )}

      {/* Error */}
      {error && <div className="pv-error">{error}</div>}

      {/* Loading overlay */}
      {loading && !stats && (
        <div className="pv-loading">Loading data...</div>
      )}

      {/* Stats cards */}
      {stats && (
        <div className="pv-stats-grid">
          {/* Row 1 */}
          <div className="pv-card pv-card-cyan">
            <div className="pv-card-label">Approved Volume</div>
            <div className="pv-card-value">{formatCents(getVal('approvedAmountEur', 'approvedAmountUsd'), currency)}</div>
            <div className="pv-card-sub">{formatNumber(stats.approvedCount)} transactions</div>
          </div>

          <div className="pv-card pv-card-red">
            <div className="pv-card-label">Declined</div>
            <div className="pv-card-value">{formatNumber(stats.declinedCount)}</div>
            <div className="pv-card-sub">transactions</div>
          </div>

          <div className="pv-card pv-card-blue">
            <div className="pv-card-label">Approval Rate</div>
            <div className="pv-card-value">{stats.approvalRate}%</div>
            <div className="pv-card-sub">{formatNumber(stats.approvedCount)} of {formatNumber(stats.approvedCount + stats.declinedCount)}</div>
          </div>

          {/* Row 2 */}
          <div className="pv-card pv-card-emerald pv-card-highlight">
            <div className="pv-card-label">Net Revenue</div>
            <div className="pv-card-value">{formatCents(getVal('netProfitEur', 'netProfitUsd'), currency)}</div>
            <div className="pv-card-sub">After all deductions</div>
          </div>

          {/* Row 3 */}
          <div className="pv-card pv-card-orange">
            <div className="pv-card-label">Chargebacks</div>
            <div className="pv-card-value">{formatNumber(stats.chargebackCount)}</div>
            <div className="pv-card-sub">{formatCents(getVal('chargebackAmountEur', 'chargebackAmountUsd'), currency)} volume</div>
          </div>

          <div className="pv-card pv-card-teal">
            <div className="pv-card-label">Payouts</div>
            <div className="pv-card-value">{formatNumber(stats.payoutCount)}</div>
            <div className="pv-card-sub">{formatCents(getVal('payoutAmountEur', 'payoutAmountUsd'), currency)} volume</div>
          </div>

          {/* Row 4: RR + Debts summaries */}
          <div className="pv-card pv-card-purple">
            <div className="pv-card-label">Total RR Held Outside</div>
            <div className="pv-card-value">{(() => {
              const eurTotal = rollingReserves.filter(r => (r.currency||'EUR')==='EUR').reduce((s,r) => s + (Number(r.held_amount_cents)||0), 0);
              const usdTotal = rollingReserves.filter(r => r.currency==='USD').reduce((s,r) => s + (Number(r.held_amount_cents)||0), 0);
              const parts = [];
              if (eurTotal > 0) parts.push(formatCents(eurTotal, 'EUR'));
              if (usdTotal > 0) parts.push(formatCents(usdTotal, 'USD'));
              return parts.length > 0 ? parts.join(' + ') : formatCents(0, 'EUR');
            })()}</div>
            <div className="pv-card-sub">{rollingReserves.length} provider{rollingReserves.length !== 1 ? 's' : ''}</div>
          </div>

          <div className="pv-card pv-card-pink">
            <div className="pv-card-label">Total Debts Outside</div>
            <div className="pv-card-value">{(() => {
              const eurTotal = debts.filter(d => (d.currency||'EUR')==='EUR').reduce((s,d) => s + (Number(d.remaining_amount_cents)||0), 0);
              const usdTotal = debts.filter(d => d.currency==='USD').reduce((s,d) => s + (Number(d.remaining_amount_cents)||0), 0);
              const parts = [];
              if (eurTotal > 0) parts.push(formatCents(eurTotal, 'EUR'));
              if (usdTotal > 0) parts.push(formatCents(usdTotal, 'USD'));
              return parts.length > 0 ? parts.join(' + ') : formatCents(0, 'EUR');
            })()}</div>
            <div className="pv-card-sub">{debts.length} debt{debts.length !== 1 ? 's' : ''} outstanding</div>
          </div>
        </div>
      )}

      {/* Rate info */}
      {stats && (
        <div className="pv-rate-info">
          EUR/USD Rate: {stats.eurUsdRate} (period average)
          {updatedAt && ` · Updated ${updatedAt.toLocaleTimeString('en-GB', { timeZone: TIMEZONE })} (Cyprus time)`}
          {loading && <span className="pv-refreshing"> Refreshing...</span>}
        </div>
      )}

      {/* ── Rolling Reserve Held Outside (read-only, managed from memcules.com) ── */}
      <div className="pv-section">
        <div className="pv-section-header">
          <h2 className="pv-section-title">Rolling Reserve Held Outside</h2>
        </div>
        {rollingReserves.length === 0 ? (
          <div className="pv-empty">No rolling reserves recorded</div>
        ) : (
          <>
            <table className="pv-table">
              <thead>
                <tr>
                  <th>Provider</th>
                  <th>Amount Held</th>
                  <th>Currency</th>
                  <th>Last Approved Transaction</th>
                  <th title="6 months after the provider's last approved transaction">Release Date</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {rollingReserves.map(rr => {
                  const release = rr.release_date ? rr.release_date.split('T')[0] : null;
                  const today = fmtDateUTC(getTzNow(TIMEZONE));
                  return (
                  <tr key={rr.id}>
                    <td>{rr.provider_name}</td>
                    <td className="pv-amount">{formatCents(rr.held_amount_cents, rr.currency || 'EUR')}</td>
                    <td>{rr.currency || 'EUR'}</td>
                    <td>{rr.last_approved_at ? new Date(rr.last_approved_at).toLocaleDateString('en-CA', { timeZone: TIMEZONE }) : '-'}</td>
                    <td>
                      {release || '-'}
                      {release && release <= today && <span className="pv-due-badge">Due</span>}
                    </td>
                    <td className="pv-notes-cell">{rr.notes || '-'}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="pv-table-summary">
              Total RR Held: {(() => {
                const eurT = rollingReserves.filter(r => (r.currency||'EUR')==='EUR').reduce((s,r) => s + (Number(r.held_amount_cents)||0), 0);
                const usdT = rollingReserves.filter(r => r.currency==='USD').reduce((s,r) => s + (Number(r.held_amount_cents)||0), 0);
                const p = [];
                if (eurT > 0) p.push(formatCents(eurT, 'EUR'));
                if (usdT > 0) p.push(formatCents(usdT, 'USD'));
                return p.length > 0 ? p.join(' + ') : formatCents(0, 'EUR');
              })()}
            </div>
          </>
        )}
      </div>

      {/* ── Our Debts Outside (read-only, managed from memcules.com) ── */}
      <div className="pv-section">
        <div className="pv-section-header">
          <h2 className="pv-section-title">Our Debts Outside</h2>
        </div>
        {debts.length === 0 ? (
          <div className="pv-empty">No debts recorded</div>
        ) : (
          <>
            <table className="pv-table">
              <thead>
                <tr>
                  <th>Who Owes Us?</th>
                  <th>Original Debt</th>
                  <th>How Much Left?</th>
                  <th>Expected Payment</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {debts.map(d => (
                  <tr key={d.id}>
                    <td>{d.creditor_name}</td>
                    <td className="pv-amount">{formatCents(d.original_amount_cents, d.currency || 'EUR')}</td>
                    <td className="pv-amount">{formatCents(d.remaining_amount_cents, d.currency || 'EUR')}</td>
                    <td>{d.expected_payment_date ? d.expected_payment_date.split('T')[0] : '-'}</td>
                    <td className="pv-notes-cell">{d.notes || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="pv-table-summary">
              Total Outstanding: {(() => {
                const eurT = debts.filter(d => (d.currency||'EUR')==='EUR').reduce((s,d) => s + (Number(d.remaining_amount_cents)||0), 0);
                const usdT = debts.filter(d => d.currency==='USD').reduce((s,d) => s + (Number(d.remaining_amount_cents)||0), 0);
                const p = [];
                if (eurT > 0) p.push(formatCents(eurT, 'EUR'));
                if (usdT > 0) p.push(formatCents(usdT, 'USD'));
                return p.length > 0 ? p.join(' + ') : formatCents(0, 'EUR');
              })()}
            </div>
          </>
        )}
      </div>
      </>)}
    </div>
  );
}

export default PartnerDashboard;
